/**
 * Mini 3D NPC viewer for the dialog panel — a small, self-contained WebGL
 * scene rendering the NPC's ACTUAL rigged body playing the shared stand idle
 * (breathing + occasional head turn via `createNpcAnimator`), slowly swaying
 * toward/away from the camera. When the NPC has no body model, or loading it
 * fails, the viewport is simply left empty (transparent) — the NPC's 2D
 * portrait PNG behind the canvas (see dialog-ui) is what shows through.
 *
 * Mounted fresh each time a conversation opens (`mount`) and fully disposed
 * on close (`dispose`) — SPA remounts must never leak a WebGL context. The
 * canvas has a transparent clear colour so the NPC's 2D portrait PNG (set as
 * the CSS background behind it in dialog-ui) shows through as a backdrop.
 *
 * Disposal note: the body model is a cache-owned clone from
 * `loadCharacterModel` — its geometry/materials/textures are SHARED with the
 * in-world instances (see scene.ts's `sharedAsset` handling), so this viewer
 * must only detach it, never `disposeModel()` it.
 */
import * as THREE from 'three';
import { loadCharacterModel } from '../../character-select/load-model';
import { createNpcAnimator, type NpcAnimator } from '../render/character-animations';

export class NpcViewer {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  /** The NPC's real body (cache-owned clone) + its idle animator, when a model URL was given and loaded. */
  private model: THREE.Object3D | null = null;
  private animator: NpcAnimator | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ro: ResizeObserver | null = null;
  private raf = 0;
  private clock = 0;
  private lastTime = 0;
  private disposed = false;
  private reducedMotionQuery: MediaQueryList | null = null;
  private readonly onReducedMotionChange = (): void => this.applyMotionPreference();

  /**
   * Mount into `host` (the portrait box). Fails safe if WebGL is unavailable —
   * the PNG backdrop remains alone.
   *
   * `onBodyShown` fires once, the moment the real rigged body is in the scene.
   * It is how the caller knows to take the 2D portrait down: that PNG is the
   * FALLBACK for an NPC with no body model, and leaving it up behind a loaded
   * body puts two characters in the box at once.
   */
  mount(
    host: HTMLElement,
    npcId: string,
    bodyModelUrl?: string,
    onBodyShown?: () => void,
  ): void {
    let renderer: THREE.WebGLRenderer;
    try {
      const canvas = document.createElement('canvas');
      canvas.className = 'bv-npc-canvas';
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'low-power' });
    } catch {
      return;
    }
    renderer.setClearColor(0x000000, 0);
    this.renderer = renderer;
    this.canvas = renderer.domElement;
    host.appendChild(this.canvas);

    const scene = new THREE.Scene();
    this.scene = scene;

    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 20);
    camera.position.set(0, 1.44, 2.3);
    camera.lookAt(0, 1.22, 0); // 6cm up from 2026-10-14: the taller portrait plate left the crown touching the frame
    this.camera = camera;

    // Warm firelight key + cool fill, matching the settlement's mood.
    const fill = new THREE.HemisphereLight(0x9fb8c9, 0x140d08, 0.55);
    scene.add(fill);
    const keyLight = new THREE.DirectionalLight(0xffb27a, 1.15);
    keyLight.position.set(1.6, 2.4, 1.8);
    scene.add(keyLight);
    const rim = new THREE.DirectionalLight(0x6d8fa0, 0.4);
    rim.position.set(-2, 1.4, -1.5);
    scene.add(rim);

    if (bodyModelUrl) {
      void loadCharacterModel(bodyModelUrl, 1.8)
        .then((model) => {
          if (this.disposed || !this.scene) return;
          model.rotation.y = -0.25;
          this.scene.add(model);
          this.model = model;
          this.animator = createNpcAnimator(model, 'stand', npcId);
          // Only now — not at mount. The load is async and can fail, and a
          // portrait hidden before the body arrives leaves an empty box.
          onBodyShown?.();
          this.renderFrame();
        })
        .catch((err) => console.warn('[npc-viewer] body model failed to load', err));
    }

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(host);
    this.resize();

    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);
    this.applyMotionPreference();
  }

  private resize(): void {
    if (!this.renderer || !this.camera || !this.canvas) return;
    const host = this.canvas.parentElement;
    const w = Math.max(1, host?.clientWidth ?? 1);
    const h = Math.max(1, host?.clientHeight ?? 1);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderFrame();
  }

  /**
   * Reduced motion: no sway/turn, and the RAF loop stops once the idle pose
   * has settled (clips load async, so a handful of frames are still needed to
   * get OUT of the bind pose — a frozen T-pose would be worse than brief
   * motion). Normal motion: continuous loop with the idle + gentle sway.
   */
  private applyMotionPreference(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.lastTime = 0;
    const reduced = this.reducedMotionQuery?.matches ?? false;
    const settleUntil = reduced ? this.clock + 2.5 : Infinity;
    const loop = (now: number): void => {
      if (this.clock > settleUntil) {
        this.raf = 0;
        return;
      }
      this.raf = requestAnimationFrame(loop);
      this.tick(now, !reduced);
    };
    this.raf = requestAnimationFrame(loop);
  }

  private tick(now: number, sway: boolean): void {
    const dt = this.lastTime ? Math.min((now - this.lastTime) / 1000, 0.05) : 0;
    this.lastTime = now;
    this.clock += dt;
    this.animator?.update(dt, false);
    const swayY = sway ? Math.sin(this.clock * 0.6) * 0.35 - 0.25 : -0.25;
    if (this.model) this.model.rotation.y = swayY;
    this.renderFrame();
  }

  private renderFrame(): void {
    if (!this.renderer || !this.scene || !this.camera) return;
    this.renderer.render(this.scene, this.camera);
  }

  /** Fully dispose GPU resources + DOM + listeners. Safe to call multiple times. */
  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.raf = 0;

    if (this.reducedMotionQuery) {
      this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
      this.reducedMotionQuery = null;
    }
    this.ro?.disconnect();
    this.ro = null;

    this.animator?.dispose();
    this.animator = null;
    // Cache-owned clone: detach only — geometry/materials are shared with the
    // in-world NPC bodies and the next dialog's clone.
    this.model?.removeFromParent();
    this.model = null;

    this.scene?.clear();
    this.scene = null;
    this.camera = null;

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      this.renderer = null;
    }
    this.canvas?.remove();
    this.canvas = null;

    this.clock = 0;
    this.lastTime = 0;
  }
}
